import { applyMiddleware, combineReducers, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import assets from './reducers/assets';
import auth from './reducers/auth';
import contacts from './reducers/contacts';
import items from './reducers/items';
import itemForm from './reducers/itemForm';
import persistence from './middlewares/persistence';
import contactForm from './reducers/contactForm';
import properties from './reducers/properties';
import propertyForm from './reducers/propertyForm';
import serviceRequest from './reducers/serviceRequest';
import serviceRequests from './reducers/serviceRequests';
import paginationCount from './reducers/Pagination';
import serviceRequestStatusPageCount from './reducers/ServiceRequestStatusPageCount';
import reports from './reducers/Reports';
import availableServiceTimes from './reducers/availableServiceTimes';
import suites from './reducers/suites';
import userSuites from './reducers/userSuites';
import typeahead from './reducers/typeahead';
import formOptions from './reducers/formOptions';
import faqsNotesDocuments from './reducers/faqsNotesDocuments';
import fndForm from './reducers/fndForm';
import assetForm from './reducers/assetForm';
import search from './reducers/search';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import Messages from './reducers/Messages';
import settings from './reducers/settings';
import suiteAreas from './reducers/suiteAreas';
import suiteAssetAreas from './reducers/suiteAssetAreas';
import propertyWalkThroughForm from './reducers/propertyWalkThroughForm';
import easyWalkThroughForm from './reducers/easyWalkThroughForm';
import areas from './reducers/areas';
import walkthroughtSuites from './reducers/WalkthroughSuites';
import loader from './reducers/loader';
import validation from './reducers/Validation';
import recentActivity from './reducers/RecentActivity';
import propertyLoader from './reducers/PropertyLoader';
import formLoader from './reducers/FormLoader';
import 'react-toastify/dist/ReactToastify.css';
import AnnouncementForm from './reducers/AnnouncementForm';
import announcements from './reducers/Announcements';
import walkThrough from './reducers/walkThroughs';
import commonAreas from './reducers/commonAreas';
import propertyCommonAreas from './reducers/propertyCommonAreas';
import suiteUsers from './reducers/suiteUsers';
import OverviewStatus from './reducers/OverviewStatus';
import commonIssues from './reducers/CommonIssues';
import commonAreaAssets from './reducers/commonAreaAssets';
import commonAreaAssetForm from './reducers/commonAreaAssetForm';
import propertyDevelopers from './reducers/propertyDevelopers';
import counts from './reducers/Counts';
import csvExport from './reducers/csvExport';
import PropertyVideos from './reducers/PropertyVideos';
import collapseSideBar from './reducers/collapseSideBar';
import buildings from './reducers/buildings';
import levels from './reducers/levels';
import homeownerOnboardingForm from './reducers/HomeownerOnboardingComponent';
import homeownerOnboarding from './reducers/HomeownerOnboarding';
import suiteStatuses from './reducers/SuiteStatus';
import constructionStages from './reducers/ConstructionStages';
import moreDetails from './reducers/MoreDetails';
import onboardingData from './reducers/HomeOwnerOnboardingDashboard';
import Filter from './reducers/Filter';
import keyContacts from './reducers/KeyContacts';
import responseStore from './reducers/ResponseStore';
import specifications from './reducers/Specifications';
import tabSelections from './reducers/tabSelections';
import FilterCommonAreas from './reducers/FilterCommonAreas';
import keyPickup from './reducers/KeyPickup';
import activeTab from './reducers/activeTab';
import { TYPES } from '../common/constants';
import emailTemplates from './reducers/emailTemplates';
import feedbackReport from './reducers/feedbackReport';
import notes from './reducers/notes';
import serviceRequestStats from './reducers/ServiceRequestStatsReports';
import sidebarPermissions from './slices/sidebarPermissionsSlice';
import serviceRequestFilters from './slices/serviceRequestFiltersSlice';
import persistedUi from './slices/persistedUiSlice';
import inboxUnreadCount from './slices/inboxUnreadCountSlice';

const persistConfig = {
  key: 'root',
  storage: storage,
  stateReconciler: autoMergeLevel2, // see "Merge Process" section for details.
};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const appReducer = combineReducers({
  assets,
  auth,
  contacts,
  contactForm,
  items,
  itemForm,
  properties,
  propertyForm,
  serviceRequest,
  serviceRequests,
  serviceRequestStats,
  suites,
  userSuites,
  typeahead,
  formOptions,
  faqsNotesDocuments,
  fndForm,
  assetForm,
  search,
  availableServiceTimes,
  Messages,
  settings,
  suiteAreas,
  suiteAssetAreas,
  propertyWalkThroughForm,
  easyWalkThroughForm,
  areas,
  loader,
  walkthroughtSuites,
  validation,
  recentActivity,
  propertyLoader,
  formLoader,
  AnnouncementForm,
  announcements,
  walkThrough,
  commonAreas,
  propertyCommonAreas,
  suiteUsers,
  OverviewStatus,
  commonIssues,
  commonAreaAssets,
  commonAreaAssetForm,
  propertyDevelopers,
  paginationCount,
  serviceRequestStatusPageCount,
  reports,
  counts,
  csvExport,
  PropertyVideos,
  collapseSideBar,
  buildings,
  levels,
  homeownerOnboardingForm,
  homeownerOnboarding,
  suiteStatuses,
  constructionStages,
  moreDetails,
  onboardingData,
  Filter,
  keyContacts,
  responseStore,
  specifications,
  tabSelections,
  FilterCommonAreas,
  keyPickup,
  activeTab,
  emailTemplates,
  feedbackReport,
  notes,
  sidebarPermissions,
  serviceRequestFilters,
  persistedUi,
  inboxUnreadCount,
});

// Sign-out resets every slice to its initialState by passing `state = undefined`
// down to combineReducers. Without this, in-memory state from the prior user
// (e.g., recent property selection, cached service requests) would leak into
// the next session — only persisted state is cleared by persistor.purge().
const rootReducer = (state, action) => {
  if (action.type === TYPES.AUTH_SIGN_OUT) {
    state = undefined;
  }

  return appReducer(state, action);
};

const pReducer = persistReducer(persistConfig, rootReducer);

export const store = createStore(
  pReducer,
  {
    auth: JSON.parse(localStorage.getItem('auth')),
  },
  composeEnhancers(
    applyMiddleware(thunk, persistence)
  )
);

export const persistor = persistStore(store);
